import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { UrlTree } from '@angular/router';

function checkFunction(code: string): Observable<boolean | UrlTree> {
  const permissions = inject(PermissionService);
  const router = inject(Router);
  return permissions.ensureLoaded().pipe(
    map((p) => (p.isSuperAdmin || permissions.has(code) ? true : router.createUrlTree(['/']))),
    catchError(() => of(router.createUrlTree(['/'])))
  );
}

/**
 * Route guard factory for the V2 user-group permission system. Use AFTER `authGuard` (which
 * establishes the JWT): `canActivate: [authGuard, requiresFunction('customers.view')]`.
 *
 * Redirects to the home dashboard when the caller's usergroup lacks the function. UX only —
 * the backend `[RequiresFunction]` filter is the real enforcement.
 */
export function requiresFunction(code: string): CanActivateFn {
  return () => checkFunction(code);
}

/**
 * Settings-page guard that derives the function code from the route path by convention:
 * `settings` → `settings.view`, `settings/<slug>` → `settings.<slug>.view`. Every settings
 * route uses this one guard, and each new settings page only needs its `AppFunction` seed row —
 * the code falls out of the route slug automatically.
 */
export const settingsPageView: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const path = route.routeConfig?.path ?? 'settings';
  const code = path === 'settings' ? 'settings.view' : `settings.${path.replace(/^settings\//, '')}.view`;
  return checkFunction(code);
};
